import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import { message, Popover } from "antd";
import { EditOutlined } from "@ant-design/icons";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import usersApis from "../../../api/master/users";
import contractorsApis from "../../../api/master/contractors";
import { fetchSequenceAccess } from "../../../store/slices/misc/sequenceAccess";
import Swal from "sweetalert2";
import WorkOrderApprovalDrawer from "../../work-requests/workRequestCreation/WorkOrderApprovalDrawer";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkBoqGeneralDetails: React.FC = (props: any) => {
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );
  const dispatch: any = useDispatch();
  
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>([]);
  const [materialBoqOptions, setMaterialBoqOptions] = useState<SelectOptions[]>([]);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isFormDataFetched, setIsFormDataFetched] = useState(false);
  
  const siteIdWatch = watch("siteId");
  const workLevelWatch = watch("workLevel");
  const approvalRequiredWatch = watch("approvalRequired");
  const AllFormDetails = watch();

  // Completion alert percentage options (5% intervals)
  const completionAlertOptions = Array.from({ length: 20 }, (_, i) => {
    const value = (i + 1) * 5;
    return { label: `${value}%`, value: value.toString() };
  });

  // Work level options as per FSD
  const workLevelOptions = [
    { label: "Project", value: "Project" },
    { label: "Tower", value: "Tower" },
    { label: "Villa", value: "Villa" },
    { label: "Floor", value: "Floor" },
    { label: "Flat", value: "Flat" },
  ];

  useEffect(() => {
    dispatch(fetchSequenceAccess());
    fetchSites();
    fetchMaterialBoqOptions();
  }, [dispatch]);

  useEffect(() => {
    if (siteIdWatch) {
      fetchProjectsForSite(siteIdWatch);
      fetchContractorsForSite(siteIdWatch);
    }
  }, [siteIdWatch]);

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        const sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log("Error fetching sites:", error);
    }
  };

  const fetchProjectsForSite = async (siteId: string) => {
    try {
      // Mock project data - replace with actual API call
      const mockProjects = [
        { label: "Residential Complex A", value: "1" },
        { label: "Commercial Building B", value: "2" },
        { label: "Infrastructure Project C", value: "3" },
      ];
      setProjectOptions(mockProjects);
    } catch (error) {
      console.log("Error fetching projects:", error);
    }
  };

  const fetchContractorsForSite = async (siteId: string) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteId,
      });
      if (status) {
        const contractorOptions = data.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }
    } catch (error) {
      console.log("Error fetching contractors:", error);
    }
  };

  const fetchMaterialBoqOptions = async () => {
    try {
      // Mock material BOQ data - replace with actual API call
      const mockMaterialBoqs = [
        { label: "Construction Materials BOQ #001", value: "1" },
        { label: "Electrical Components BOQ #002", value: "2" },
        { label: "Plumbing Materials BOQ #003", value: "3" },
      ];
      setMaterialBoqOptions(mockMaterialBoqs);
    } catch (error) {
      console.log("Error fetching material BOQs:", error);
    }
  };

  const validateForm = (data: any) => {
    if (!data.boqTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "BOQ Title is required",
      });
      return false;
    }

    if (data.boqTitle.length > 100) {
      Swal.fire({
        icon: "error",
        title: "Validation Error", 
        text: "BOQ Title cannot exceed 100 characters",
      });
      return false;
    }

    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site Selection is required",
      });
      return false;
    }

    if (!data.materialBoqId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Material BOQ Selection is required",
      });
      return false;
    }

    if (!data.workLevel) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Level Settings is required",
      });
      return false;
    }

    // Approval Required validation
    if (!data.approvalRequired) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please specify if approval is required",
      });
      return false;
    }

    return true;
  };

  const setFormValues = () => {
    const { approvalsData } = AllFormDetails;
    
    if (approvalsData) {
      const _approvals = JSON.parse(approvalsData);
      setApprovals(_approvals);
    }
  };

  useEffect(() => {
    if (!isFormDataFetched && Object.keys(AllFormDetails).length > 0) {
      setFormValues();
      setIsFormDataFetched(true);
    }
  }, [AllFormDetails]);

  const submitForm = async (data: any) => {
    if (!validateForm(data)) return;

    // Set approvals data in form
    setValue("approvalsData", JSON.stringify(approvals));
    
    // Only block navigation if approval is required ("true") but no approvals set
    if (data.approvalRequired === "true" && approvals?.data?.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Approval Required",
        text: "Please set the required approvals before proceeding to the next step.",
      });
      return;
    }

    const payload = {
      ...data,
      approvalsData: JSON.stringify(approvals),
      step: "generalDetails",
    };

    console.log("General Details Payload:", payload);
    props.next(payload);
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <div className="card">
          <div className="card-body">
            <Row className="gy-4">
              <Col lg="6">
                <FormInput
                  name="boqTitle"
                  label="BOQ Title"
                  required={true}
                  placeholder="Enter BOQ Title (Max 100 characters)"
                  maxLength={100}
                  tooltipText="Title or Name for the BOQ"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="siteId"
                  label="Site Selection"
                  options={sitesOptions}
                  required={true}
                  placeholder="Select Site"
                  tooltipText="Select from available construction sites"
                />
              </Col>
              
              <Col lg="12">
                <FormTextArea
                  name="boqDescription"
                  label="BOQ Description"
                  placeholder="Enter detailed description of the BOQ (Max 500 characters)"
                  maxLength={500}
                  rows={3}
                  // tooltipText="Detailed description of the BOQ"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="projectId"
                  label="Project Selection"
                  options={projectOptions}
                  placeholder="Select Project (Optional)"
                  tooltipText="Projects assigned to the current user"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="contractorId"
                  label="Contractor Selection"
                  options={contractorOptions}
                  placeholder="Select Contractor (Optional)"
                  tooltipText="Contractors onboarded in the system"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="completionAlertsFrom"
                  label="Completion Alerts From"
                  options={completionAlertOptions}
                  placeholder="Select Alert Percentage"
                  tooltipText="Alert level based on completion percentage (5%, 10%, etc.)"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="materialBoqId"
                  label="Select Material BOQ"
                  options={materialBoqOptions}
                  required={true}
                  placeholder="Select Material BOQ"
                  tooltipText="Link to existing Material BOQs"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="workLevel"
                  label="Work Level Settings"
                  options={workLevelOptions}
                  required={true}
                  placeholder="Select Work Level"
                  tooltipText="Define level of BOQ: Project/Tower/Villa/Floor/Flat"
                />
              </Col>

              {/* Approval Required - Mandatory Boolean */}
              <Col lg="6">
                <FormSelect
                  name="approvalRequired"
                  label="Approval Required ?"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  placeholder="Select"
                  disabled={!siteIdWatch}
                  required={true}
                  tooltipText="If Yes, configure approval hierarchy as per reference path"
                />
                
                {/* Hidden field for approvals data */}
                <div style={{ display: "none" }}>
                  <FormInput name="approvalsData" label="" type="text" />
                </div>
                
                {/* Set Approvals Link */}
                {approvalRequiredWatch === "true" && (
                  <Popover
                    content="Click here to set required approvals"
                    title="Approval Required"
                  >
                    <a
                      href="#"
                      className="success"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!siteIdWatch) {
                          message.error("Please select a site first");
                          return;
                        }
                        setApprovals((pre) => {
                          return {
                            ...pre,
                            open: true,
                          };
                        });
                      }}
                    >
                      <EditOutlined /> Set Approvals &nbsp;
                      {approvals?.data?.length === 0 ? (
                        <span className="mandatory">Please set approvals</span>
                      ) : (
                        <span className="text-success">
                          ✓ {approvals.data.length} approval(s) configured
                        </span>
                      )}
                    </a>
                  </Popover>
                )}
              </Col>
            </Row>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
          </ul>
        </div>
      </form>
      
      {/* Work Order Approval Drawer */}
      {approvals.open && (
        <WorkOrderApprovalDrawer
          open={approvals.open}
          siteId={parseInt(siteIdWatch) || 0}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any) => {
            setApprovals({
              open: false,
              data: data,
            });
          }}
          setApprovalSubmissionStatus={(status: boolean) => {}}
          approvalData={approvals.data}
        />
      )}
    </>
  );
};

export default WorkBoqGeneralDetails;